import React from "react";
import { createRoot } from "react-dom/client";
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  ApolloLink,
} from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { ErrorLink } from "@apollo/client/link/error";
import { CombinedGraphQLErrors } from "@apollo/client/errors";

import environment from "./config";
import { LogsProvider } from "context/logs/logs.provider";
import { AuthProvider } from "./context/auth/auth.provider";
import { HostProvider } from "./context/host/host.provider";
import { AgenciaProvider } from "./context/agencia/agencia.provider";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import mgTheme from "./assets/mgTheme";

import App from "./components/app/App";

import { unregister } from "./registerServiceWorker";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import LDUserIdentifier from "components/app/featureFlags/LDUserIdentifier";

// Delete me!

const authLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem("accessToken");
  operation.setContext({
    headers: {
      authorization: token ? `Bearer ${token}` : "",
    },
  });
  return forward(operation);
});

const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  } else {
    console.log(`[Network error]: ${error}`);
  }
});

const client = new ApolloClient({
  link: ApolloLink.from([
    authLink,
    errorLink,
    new HttpLink({
      uri: process.env.REACT_APP_API_MARKETPLACE_URL + "/graphql",
      credentials: "same-origin",
    }),
  ]),

  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          misPublicaciones: {
            keyArgs: ["estado"],
            merge(existing, incoming, { args: { offset = 0 } }) {
              if (offset === 0) return incoming;

              const merged = existing ? existing.items.slice(0) : [];
              for (let i = 0; i < incoming.items.length; ++i) {
                merged[offset + i] = incoming.items[i];
              }

              return {
                hasMore: incoming.hasMore,
                items: merged,
                totalCount: incoming.totalCount,
                __typename: incoming.__typename,
              };
            },
          },
        },
      },
    },
  }),
});

declare global {
  interface Window {
    render_front_autos: Function;
    unmount_front_autos: Function;
  }
}

const ExtendedApp = async (host, history) => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: process.env.REACT_APP_LAUNCH_DARKLY_SDK_KEY,
    options: { streaming: process.env.REACT_APP_ENV === "test" ? false : true },
  });

  return (
    <React.StrictMode>
      <LogsProvider>
        <ApolloProvider client={client as any}>
          <AuthProvider
            domain={environment.auth0.domain ?? ""}
            clientId={environment.auth0.clientId ?? ""}
          >
            <HostProvider host={host}>
              <AgenciaProvider>
                <LDProvider>
                  <LDUserIdentifier />
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={mgTheme}>
                      <CssBaseline />
                      <App history={history} />
                    </ThemeProvider>
                  </StyledEngineProvider>
                </LDProvider>
              </AgenciaProvider>
            </HostProvider>
          </AuthProvider>
        </ApolloProvider>
      </LogsProvider>
    </React.StrictMode>
  );
};

const rootStore = new Map();

window.render_front_autos = async (containerId, host, history) => {
  const container = document.getElementById(containerId);

  if (rootStore.has(containerId)) {
    rootStore.get(containerId).unmount();
  }

  const root = createRoot(container);
  rootStore.set(containerId, root);

  root.render(await ExtendedApp(host, history));
  unregister();
};

window.unmount_front_autos = (containerId) => {
  if (rootStore.has(containerId)) {
    const root = rootStore.get(containerId);

    root.unmount();
    rootStore.delete(containerId);
  }
};
